import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import UserList from '../components/admin/UserList';
import UserForm from '../components/admin/UserForm';
import Sidebar from '../components/admin/Sidebar';
import api from '../utils/api';
import '../styles/Admin.css';

interface Role {
  __enumName__: string;
  _code: string;
  _name: string;
}

interface User {
  id: string;
  username: string;
  isActive: boolean;
  role: Role;
  school: string;
  createdAt: string;
}

const AdminPage: React.FC = () => {
  const { user, loading } = useAuth();
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>();
  const [error, setError] = useState<string | null>(null);

  // 로딩 중이면 로딩 표시
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // 사용자가 없거나 ADMIN이 아닌 경우 메인 페이지로 리다이렉트
  if (!user || user.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  const handleCreateUser = async (userData: any) => {
    try {
      await api.post('/users', userData);
      setShowUserForm(false);
      setError(null);
      // 사용자 목록 새로고침을 위해 페이지 새로고침
      window.location.reload();
    } catch (err) {
      setError('사용자 생성에 실패했습니다.');
    }
  };

  const handleEditUser = async (userData: any) => {
    try {
      if (editingUser?.id) {
        await api.patch(`/users/${editingUser.id}`, userData);
        setShowUserForm(false);
        setEditingUser(undefined);
        setError(null);
        // 사용자 목록 새로고침을 위해 페이지 새로고침
        window.location.reload();
      }
    } catch (err) {
      setError('사용자 수정에 실패했습니다.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      try {
        await api.delete(`/users/${userId}`);
        setError(null);
        // 사용자 목록 새로고침을 위해 페이지 새로고침
        window.location.reload();
      } catch (err) {
        setError('사용자 삭제에 실패했습니다.');
      }
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  return (
    <div className="admin-container">
      <Sidebar />

      <div className="admin-content">
        <header className="admin-header">
          <div className="admin-header-content">
            <div>
              <h1 className="admin-title">사용자 관리</h1>
              <p className="admin-subtitle">시스템의 모든 사용자를 관리합니다</p>
            </div>
          </div>
        </header>

        <main className="admin-main">
          {error && (
            <div className="error-message">
              <span>⚠️</span>
              {error}
            </div>
          )}

          <div className="admin-card">
            <div className="admin-card-header">
              <div className="admin-header-content">
                <h2 className="admin-card-title">사용자 목록</h2>
                <button
                  onClick={() => {
                    setEditingUser(undefined);
                    setShowUserForm(true);
                  }}
                  className="btn btn-primary"
                >
                  <span>+</span>
                  새 사용자 추가
                </button>
              </div>
            </div>

            <div className="admin-card-body">
              {showUserForm ? (
                <div>
                  <UserForm
                    user={editingUser}
                    onSubmit={editingUser ? handleEditUser : handleCreateUser}
                    onCancel={() => {
                      setShowUserForm(false);
                      setEditingUser(undefined);
                    }}
                  />
                </div>
              ) : (
                <UserList
                  onEditUser={handleEditClick}
                  onDeleteUser={handleDeleteUser}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
